import { useTranslation } from 'react-i18next';
import Box from '@mui/material/Box';
import Table from '@mui/material/Table';
import TableContainer from '@mui/material/TableContainer';
import TableRow from '@mui/material/TableRow';
import TableBody from '@mui/material/TableBody';
import Typography from '@mui/material/Typography';
import { useEffect, useMemo } from 'react';

import { TableActionBar, TableCell, TableHead } from 'common/components/Table';
import { IUserListItemFragment } from 'graphql/types';
import { FilterBar } from 'common/components/FilterBar';
import { IconButton } from 'common/components/IconButton';
import { Tooltip } from 'common/components/Tooltip';
import { Loader } from 'common/components/Loader';
import {
    IPaginationProps,
    ISearchProps,
    ITableSelectionProps,
    TFilterBarItem,
    TTableAction,
} from 'common/types';
import { Pagination } from 'common/components/Pagination';
import { Checkbox } from 'common/components/Checkbox';

import { UserTableRow } from './UserTableRow';

interface IProps {
    disabled?: boolean;
    disabledUserIds?: string[];
    filterBarAppend?: React.ReactNode;
    infoMessage?: React.ReactNode;
    isEditable?: boolean;
    loading?: boolean;
    noResultsLabel?: string;
    paginationTranslationKey?: string;
    paginationProps?: IPaginationProps;
    tableActions?: TTableAction[];
    selectionProps?: ITableSelectionProps<IUserListItemFragment>;
    users?: IUserListItemFragment[];
    searchProps?: ISearchProps;
    selectedUserTable?: React.ReactNode;
    inDrawer?: boolean;
    onSelectUsers?(selectedUsers: string[]): void;
    onSelectAll?(): void;
    onSearchClear?(): void;
    onFilterChange?(): void;
}

export const UserTable = ({
    filterBarAppend,
    infoMessage,
    disabled,
    disabledUserIds,
    isEditable = true,
    loading,
    noResultsLabel,
    paginationTranslationKey,
    paginationProps,
    tableActions,
    selectionProps,
    users,
    searchProps,
    selectedUserTable,
    inDrawer,
    onSelectUsers,
    onSelectAll,
    onSearchClear,
    onFilterChange,
}: IProps) => {
    const [translate] = useTranslation();
    const {
        filters,
        searchQueryParam,
        selectedFilters,
        handleSearch: onSearch = () => {},
        handleFilterSelect: onFilterSelect,
    } = searchProps || {};
    const { paginationSettings, setPage } = paginationProps || {};
    const {
        rows = [],
        isSelected,
        selected,
        selectableItems = [],
        onSelectAll: onSelectAllUsers,
    } = selectionProps || {};

    const handleSelectAll = () => {
        if (onSelectAll) {
            onSelectAll();

            return;
        }

        onSelectAllUsers?.();
    };

    const selectedUserIds = useMemo(
        () =>
            selected?.filter(
                (selectedUserId) =>
                    !disabledUserIds?.some(
                        (disabledUserId) => selectedUserId === disabledUserId
                    )
            ) || [],
        [selected, disabledUserIds]
    );

    useEffect(() => {
        onSelectUsers?.(selectedUserIds);
    }, [selectedUserIds, onSelectUsers]);

    const actions = tableActions?.map(
        ({ tooltipTitle, iconPath, onClick }, index) => (
            <Tooltip key={index} title={tooltipTitle}>
                <Box
                    sx={{
                        display: 'flex',
                        justifyContent: 'center',
                        alignItems: 'center',
                        ml: 'auto',
                    }}
                >
                    <IconButton
                        color="inherit"
                        disabled={!isSelected}
                        iconPath={iconPath}
                        iconSize="2.4rem"
                        onClick={() => {
                            onClick?.(selectedUserIds);
                        }}
                    />
                </Box>
            </Tooltip>
        )
    );

    const handleSearch = (searchValue: string) => {
        onSearch?.(searchValue);

        if (!!selected?.length) handleSelectAll?.();

        onSearchClear?.();
    };

    const handleFilterSelect = (selectedFilters: TFilterBarItem[]) => {
        onFilterSelect?.(selectedFilters);

        if (!!selected?.length) handleSelectAll?.();

        onFilterChange?.();
    };

    // When disabledUserIds given we want to check if all the users are selected, defaults to false otherwise
    // This is used for the useSelectedUsers hook functionality
    const everyUserSelected = useMemo(
        () =>
            selectableItems.every((selectableItem) =>
                disabledUserIds?.some(
                    (disabledUserId) => selectableItem.id === disabledUserId
                )
            ),
        [selectableItems, disabledUserIds]
    );

    const disableSelectAll =
        !selectableItems.length || everyUserSelected || loading || disabled;

    return (
        <>
            {infoMessage}

            {!!searchProps && (
                <FilterBar
                    barAppend={filterBarAppend}
                    disabled={disabled}
                    filters={filters}
                    initialSearchValue={searchQueryParam}
                    initialSelected={selectedFilters}
                    placeholder={translate('filterBarPlaceholder')}
                    onSearch={handleSearch}
                    onSearchClear={() => handleSearch?.('')}
                    onSelect={handleFilterSelect}
                />
            )}

            {selectedUserTable}

            {!loading && !!rows.length && (
                <>
                    {!!selectedUserTable && (
                        <Typography sx={{ mb: 2 }} variant="h6">
                            {translate('addUsersTitle')}
                        </Typography>
                    )}

                    <TableContainer>
                        {isEditable && (
                            <TableActionBar
                                actions={actions}
                                inDrawer={inDrawer}
                                selectionProps={selectionProps}
                            />
                        )}

                        <Table>
                            <TableHead>
                                <TableRow>
                                    {isEditable && (
                                        <TableCell padding="checkbox">
                                            <Checkbox
                                                checked={false}
                                                disabled={disableSelectAll}
                                                onChange={handleSelectAll}
                                            />
                                        </TableCell>
                                    )}
                                    <TableCell>{translate('name')}</TableCell>
                                </TableRow>
                            </TableHead>

                            <TableBody>
                                {rows?.map(
                                    ({ index, onSelect, isSelected }) => {
                                        const user = users?.[index] || null;

                                        if (!user) return null;

                                        const isDisabled =
                                            disabledUserIds?.some(
                                                (disabledUserId) =>
                                                    disabledUserId === user?.id
                                            );

                                        return (
                                            <UserTableRow
                                                disabled={
                                                    isDisabled || disabled
                                                }
                                                isEditable={!isDisabled}
                                                isSelectable={isEditable}
                                                isSelected={isSelected}
                                                key={user.id}
                                                user={user}
                                                onSelect={onSelect}
                                            />
                                        );
                                    }
                                )}
                            </TableBody>
                        </Table>
                    </TableContainer>
                </>
            )}

            {loading && (
                <Box sx={{ position: 'relative', height: '100px', mt: 4 }}>
                    <Loader />
                </Box>
            )}

            {!loading && !users?.length && (
                <Box sx={{ mt: 4, textAlign: 'center' }}>
                    <Typography>
                        {noResultsLabel || translate('noOptionsText.users')}
                    </Typography>
                </Box>
            )}

            {!loading && !!paginationSettings && (
                <Box sx={{ mt: 4 }}>
                    <Pagination
                        page={paginationSettings.page}
                        pageAmount={paginationSettings.pageAmount}
                        totalsAmount={paginationSettings.count}
                        totalsText={translate(
                            paginationTranslationKey || 'user',
                            {
                                count: paginationSettings.count,
                            }
                        )}
                        onChange={(page: number) => {
                            setPage?.(page);
                        }}
                    />
                </Box>
            )}
        </>
    );
};
